<template>
    <v-dialog v-model="internalDialog" max-width="600" persistent>
        <v-card>
            <v-card-title class="text-h5">
                Create New Goal
                <v-spacer></v-spacer>
                <v-btn icon @click="closeDialog">
                    <v-icon>mdi-close</v-icon>
                </v-btn>
            </v-card-title>

            <v-card-subtitle class="pb-0">
                Add a new goal or initiative for this company
            </v-card-subtitle>

            <v-card-text>
                <v-form ref="form" v-model="valid">
                    <!-- Goal Name -->
                    <div class="mb-4">
                        <label class="text-subtitle-2 font-weight-medium mb-1 d-block">
                            Goal Name <span class="red--text">*</span>
                        </label>
                        <v-text-field 
                            v-model="formData.name" 
                            placeholder="e.g., Q4 Marketing Campaign" 
                            outlined 
                            dense
                            :rules="[rules.required, rules.maxLength]"
                        ></v-text-field>
                    </div>

                    <!-- Description -->
                    <div class="mb-4">
                        <label class="text-subtitle-2 font-weight-medium mb-1 d-block">
                            Description
                        </label>
                        <v-textarea 
                            v-model="formData.description"
                            placeholder="Describe the goal and its objectives..." 
                            outlined 
                            dense 
                            rows="3"
                            auto-grow
                        ></v-textarea>
                    </div>

                    <!-- Goal Type -->
                    <div class="mb-4">
                        <label class="text-subtitle-2 font-weight-medium mb-1 d-block">
                            Goal Type
                        </label>
                        <v-radio-group v-model="formData.goal_type" row>
                            <v-radio label="New Website" value="new_website"></v-radio>
                            <v-radio label="New Business Unit" value="new_business_unit"></v-radio>
                        </v-radio-group>
                    </div>

                    <!-- Status and Phase Row -->
                    <v-row>
                        <v-col cols="6">
                            <div class="mb-4">
                                <label class="text-subtitle-2 font-weight-medium mb-1 d-block">
                                    Status
                                </label>
                                <v-select 
                                    v-model="formData.status" 
                                    :items="statusOptions" 
                                    placeholder="Planning" 
                                    outlined
                                    dense
                                ></v-select>
                            </div>
                        </v-col>
                        <v-col cols="6">
                            <div class="mb-4">
                                <label class="text-subtitle-2 font-weight-medium mb-1 d-block">
                                    Phase
                                </label>
                                <v-text-field 
                                    v-model="formData.phase" 
                                    placeholder="e.g., Discovery, Development" 
                                    outlined 
                                    dense
                                    :rules="[rules.maxLength]"
                                ></v-text-field>
                            </div>
                        </v-col>
                    </v-row>

                    <!-- Date Row -->
                    <v-row>
                        <v-col cols="6">
                            <div class="mb-4">
                                <label class="text-subtitle-2 font-weight-medium mb-1 d-block">
                                    Start Date
                                </label>
                                <v-menu
                                    v-model="startDateMenu"
                                    :close-on-content-click="false"
                                    transition="scale-transition"
                                    offset-y
                                    min-width="auto"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-text-field
                                            v-model="formData.start_date"
                                            placeholder="Pick a date"
                                            prepend-inner-icon="mdi-calendar"
                                            readonly
                                            outlined
                                            dense
                                            v-bind="attrs"
                                            v-on="on"
                                            clearable
                                        ></v-text-field>
                                    </template>
                                    <v-date-picker
                                        v-model="formData.start_date"
                                        @input="startDateMenu = false"
                                    ></v-date-picker>
                                </v-menu>
                            </div>
                        </v-col>
                        <v-col cols="6">
                            <div class="mb-4">
                                <label class="text-subtitle-2 font-weight-medium mb-1 d-block">
                                    Deadline
                                </label>
                                <v-menu
                                    v-model="deadlineMenu"
                                    :close-on-content-click="false"
                                    transition="scale-transition"
                                    offset-y
                                    min-width="auto"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-text-field
                                            v-model="formData.deadline"
                                            placeholder="Pick a date"
                                            prepend-inner-icon="mdi-calendar"
                                            readonly
                                            outlined
                                            dense
                                            v-bind="attrs"
                                            v-on="on"
                                            clearable
                                        ></v-text-field>
                                    </template>
                                    <v-date-picker
                                        v-model="formData.deadline"
                                        @input="deadlineMenu = false"
                                    ></v-date-picker>
                                </v-menu>
                            </div>
                        </v-col>
                    </v-row>

                    <!-- Assigned To (Multiple) -->
                    <!-- <div class="mb-4">
                        <label class="text-subtitle-2 font-weight-medium mb-1 d-block">
                            Assigned To (Multiple)
                        </label>
                        <v-autocomplete 
                            v-model="formData.assignees" 
                            :items="usersList" 
                            item-text="name" 
                            item-value="id"
                            placeholder="Select people" 
                            outlined 
                            dense 
                            multiple 
                            chips 
                            small-chips 
                            deletable-chips
                        >
                            <template v-slot:selection="{ item, index }">
                                <v-chip v-if="index < 3" small close @click:close="removeAssignee(item.id)">
                                    {{ item.name }}
                                </v-chip>
                                <span v-if="index === 3" class="grey--text text-caption">
                                    (+{{ formData.assignees.length - 3 }} others)
                                </span>
                            </template>
                        </v-autocomplete>
                    </div> -->
                </v-form>
            </v-card-text>

            <v-card-actions class="px-6 pb-4">
                <v-spacer></v-spacer>
                <v-btn text @click="closeDialog" :disabled="loading">
                    Cancel
                </v-btn>
                <v-btn color="primary" @click="createGoal" :loading="loading" :disabled="!valid">
                    Create Goal
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
export default {
    name: 'CreateGoalDialog',
    inject: ['users', 'optimisticAddGoal'],
    props: {
        value: {
            type: Boolean,
            default: false
        },
        companyId: {
            type: Number,
            required: true
        }
    },
    data() {
        return {
            internalDialog: this.value,
            valid: false,
            loading: false,
            startDateMenu: false,
            deadlineMenu: false,
            formData: {
                name: '',
                description: '',
                goal_type: 'new_website',
                status: 'Planning',
                phase: '',
                start_date: null,
                deadline: null,
                assignees: [],
                company_id: this.companyId
            },
            statusOptions: ['Planning', 'Active', 'On Hold', 'Completed'],
            rules: {
                required: (v) => !!v || 'This field is required',
                maxLength: (v) => !v || v.length <= 255 || 'Maximum 255 characters'
            }
        };
    },
    computed: {
        usersList() {
            return this.users() || [];
        }
    },
    watch: {
        value(val) {
            this.internalDialog = val;
        },
        internalDialog(val) {
            this.$emit('input', val);
            if (!val) {
                this.resetForm();
            }
        },
        companyId(val) {
            this.formData.company_id = val;
        }
    },
    methods: {
        closeDialog() {
            this.internalDialog = false;
        },
        removeAssignee(userId) {
            const index = this.formData.assignees.indexOf(userId);
            if (index > -1) {
                this.formData.assignees.splice(index, 1);
            }
        },
        resetForm() {
            this.formData = {
                name: '',
                description: '',
                goal_type: 'new_website',
                status: 'Planning',
                phase: '',
                start_date: null,
                deadline: null,
                assignees: [],
                company_id: this.companyId
            };
            if (this.$refs.form) {
                this.$refs.form.resetValidation();
            }
        },
        async createGoal() {
            if (!this.$refs.form.validate()) {
                return;
            }

            this.loading = true;
            try {
                let createdGoal = null;
                
                await this.optimisticAddGoal(
                    this.formData,
                    async () => {
                        const response = await this.$axios.post('api/goals-v2/', this.formData);
                        
                        if (!response.data.response) {
                            throw new Error(response.data.message || 'Failed to create goal');
                        }
                        
                        createdGoal = response.data.data;
                        return response.data;
                    }
                );

                // Create default milestones after goal is created
                if (createdGoal && createdGoal.id) {
                    await this.createDefaultMilestones(createdGoal.id, this.formData.goal_type);
                    
                    // Fetch the updated goal with milestone count
                    try {
                        const updatedResponse = await this.$axios.get(`api/goals-v2/${createdGoal.id}`);
                        if (updatedResponse.data.response) {
                            createdGoal = updatedResponse.data.data;
                        }
                    } catch (error) {
                        console.error('Error fetching updated goal:', error);
                    }
                }

                this.$emit('goal-created', createdGoal);
                this.$toast.success('Goal created successfully with default milestones');
                this.closeDialog();
            } catch (error) {
                console.error('Error creating goal:', error);
                this.$toast.error(error.message || 'An error occurred while creating the goal');
            } finally {
                this.loading = false;
            }
        },
        async createDefaultMilestones(goalId, goalType) {
            let defaultMilestones = {};
            
            if (goalType === 'new_business_unit') {
                // New Business Unit milestones
                defaultMilestones = {
                    'Product Developers': [
                        'Mock Pkgs',
                        'Supplier Sourcing/List',
                        'Supplier Comm',
                        'PMT Uploading',
                        'Transition from BD to Sales'
                    ],
                    'Marketing': [
                        'Packages (Business Dev)',
                        'Uploaded Package',
                        'Campaign Set-up'
                    ]
                };
            } else {
                // New Website milestones
                defaultMilestones = {
                    'Product Developers': [
                        'Mock Pkgs',
                        'Supplier Sourcing/List',
                        'Supplier Comm',
                        'PMT Uploading',
                        'Transition from BD to Sales'
                    ],
                    'Marketing': [
                        'Buy Domain Name',
                        'Moodboard',
                        'Staging',
                        'Website',
                        'Non-Package Content',
                        'Images',
                        'Packages (Business Dev)',
                        'Uploaded Package',
                        'GHL-Website Integration',
                        'Package Filter',
                        'Mobile',
                        'Speed',
                        'Email',
                        'GHL Set up',
                        'Adwords-With Tracking Code',
                        'Bing-With Tracking Code',
                        'Likes FB Ads-With Tracking Code',
                        'Facebook Page',
                        'Instagram',
                        'Tiktok',
                        'Buy US Number'
                    ]
                };
            }

            try {
                // Get all user roles to find the role IDs
                const rolesResponse = await this.$axios.get('api/users/get_user_roles');
                const roles = rolesResponse.data?.data || [];
                
                const tasks = [];
                
                for (const [roleName, milestoneNames] of Object.entries(defaultMilestones)) {
                    const role = roles.find(r => r.name === roleName);
                    if (!role) {
                        console.warn(`Role "${roleName}" not found. Available roles:`, roles.map(r => r.name));
                        continue;
                    }
                    
                    milestoneNames.forEach((milestoneName) => {
                        tasks.push({
                            name: milestoneName,
                            description: '',
                            company_id: this.companyId,
                            goal_id: goalId,
                            role_id: role.id,
                            status_id: 1, // Default status (e.g., "To Do")
                            priority_id: 2, // Default priority (e.g., "Medium")
                            assignee_ids: this.formData.assignees || [],
                            due_date: null,
                            start_time: null,
                            end_time: null,
                            allocated_time: null
                        });
                    });
                }

                if (tasks.length > 0) {
                    console.log(`Creating ${tasks.length} default milestone tasks for goal ${goalId}`);
                    await this.$axios.post('api/tasks/create_task_v2', { tasks });
                }
            } catch (error) {
                console.error('Error creating default milestones:', error);
                // Don't throw - goal was created successfully, just log the error
            }
        }
    }
};
</script>

<style scoped>
.v-card-title {
    background-color: #f5f5f5;
}

label {
    color: #333;
}
</style>
